import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, Users, BarChart3, LogOut, Home, Trash2, Menu, X, ChevronRight, User, Settings, Phone, Mail, Globe, MapPin } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { logout } from '../store/auth/authSlice';
import { UserRole } from '../types';

export const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isAdmin = user?.role === UserRole.ADMIN;

  const navItems = isAdmin
    ? [
        { path: '/admin', label: 'Dashboard', icon: Home, exact: true },
        { path: '/admin/books', label: 'Manage Books', icon: BookOpen },
        { path: '/admin/reading-stats', label: 'Reading Stats', icon: BarChart3 },
        { path: '/admin/deleted-books', label: 'Deleted Books', icon: Trash2 },
        { path: '/admin/users', label: 'Manage Users', icon: Users },
        { path: '/admin/profile', label: 'My Profile', icon: User },
      ]
    : [
        { path: '/books', label: 'Browse Books', icon: BookOpen },
        { path: '/profile', label: 'My Profile', icon: User },
      ];

  const closeSidebar = () => setSidebarOpen(false);

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Hide layout header and sidebar for book reader page
  const isBookReaderPage = location.pathname.includes('/read');

  return (
    <div className="flex min-h-screen bg-primary-50">
      {/* Mobile Header */}
      {!isBookReaderPage && (
        <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-primary-800 text-white flex items-center justify-between px-4 z-40">
        <Link
          to={isAdmin ? '/admin' : '/books'}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img src="/icon.png" alt="GHAFES Logo" className="w-6 h-6 object-contain" />
          <span className="font-bold">GHAFES Library</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-primary-700 rounded-lg transition-colors"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>
      )}

      {/* Mobile Overlay */}
      {!isBookReaderPage && sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      {!isBookReaderPage && (
        <aside
          className={`
            fixed top-0 left-0 h-full bg-primary-800 text-white flex flex-col z-50
            w-64 transform transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0 lg:static lg:z-auto
            `}
          style={{ height: '-webkit-fill-available' }}
      >
        <Link
          to={isAdmin ? '/admin' : '/books'}
          onClick={closeSidebar}
          className="p-4 lg:p-6 flex items-center gap-3 border-b border-primary-700 hover:bg-primary-700 transition-colors"
        >
          <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
            <img src="/icon.png" alt="GHAFES Logo" className="w-6 h-6 object-contain" />
          </div>
          <span className="text-lg lg:text-xl font-bold">GHAFES Library</span>
        </Link>

        <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`
                  group flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-xl
                  transition-all duration-200 text-sm lg:text-base relative
                  ${active
                    ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg shadow-primary-500/20'
                    : 'text-primary-200 hover:bg-primary-700 hover:text-white'
                  }
                `}
              >
                <item.icon size={20} className={`transition-transform duration-200 ${active ? 'scale-110' : 'group-hover:scale-110'}`} />
                <span className="flex-1">{item.label}</span>
                {active && <ChevronRight size={16} className="opacity-75" />}
              </Link>
            );
          })}
        </nav>

        {/* GHAFES Organization Details */}
        <div className="px-3 lg:px-4 py-3 border-t border-primary-700 overflow-hidden">
          <div className="text-center mb-3">
            <p className="text-xs font-bold text-primary-200 tracking-wide">GHAFES</p>
            <p className="text-[10px] text-primary-300 leading-tight break-words">GHANA FELLOWSHIP OF EVANGELICAL STUDENTS</p>
          </div>
          <div className="space-y-1.5 text-[10px] text-primary-300">
            <div className="flex items-center gap-2 min-w-0">
              <Phone size={12} className="text-primary-400 flex-shrink-0" />
              <span className="truncate">+233 (0)26 464 8158/9</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Mail size={12} className="text-primary-400 flex-shrink-0" />
              <a href="mailto:info@ghafes.org" className="hover:text-white transition-colors truncate">info@ghafes.org</a>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Globe size={12} className="text-primary-400 flex-shrink-0" />
              <a href="https://www.ghafes.org" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors truncate">www.ghafes.org</a>
            </div>
            <div className="flex items-start gap-2 min-w-0">
              <MapPin size={12} className="text-primary-400 flex-shrink-0 mt-0.5" />
              <div className="leading-tight break-words min-w-0 flex-1">
                <p>GA-121-4575 | GA121</p>
                <p>#22 Odotei Tsui Avenue,</p>
                <p>Dzorwulu-Accra, Ghana</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 lg:p-4 border-t border-primary-700">
          <Link
            to={isAdmin ? '/admin/profile' : '/profile'}
            onClick={closeSidebar}
            className="flex items-center gap-3 mb-3 lg:mb-4 p-2 rounded-xl hover:bg-primary-700 transition-colors"
          >
            <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-full bg-primary-600 flex items-center justify-center overflow-hidden border-2 border-primary-500">
              {user?.profileImage ? (
                <img src={user.profileImage} alt={user.firstName} className="w-full h-full object-cover" />
              ) : (
                <span className="font-semibold text-sm lg:text-base">
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-primary-300 capitalize">{user?.role}</p>
            </div>
            <Settings size={16} className="text-primary-400" />
          </Link>
          <button
            className="flex items-center gap-2 w-full px-3 lg:px-4 py-2.5 text-primary-200 hover:bg-primary-700 hover:text-white rounded-xl transition-colors text-sm lg:text-base"
            onClick={handleLogout}
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      )}

      {/* Main Content */}
      <main className={`flex-1 min-h-screen ${isBookReaderPage ? 'pt-0' : 'pt-14 lg:pt-0'}`}>
        {isBookReaderPage ? (
          <Outlet />
        ) : (
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
            <Outlet />
          </div>
        )}
      </main>
    </div>
  );
};
